import type {
  SimEvent,
  SimResult,
  StrikeWeapon,
  Target,
  Vec2,
  World,
} from "@air-defence-sim/sim-core";
import {
  drawFixedDefence,
  drawMissile,
  drawMobileDefence,
  drawSensor,
  drawStrikeWeapon,
  drawTarget,
} from "./sprites.js";
import { getPalette, type Palette, type ThemeName } from "./theme.js";

const MISSILE_FLIGHT_T = 0.6;

export type HighlightKind = "target" | "fixed" | "mobile" | "sensor" | "weapon" | null;

export type Renderer = {
  drawFrame: (simT: number, alpha: number) => void;
  setResult: (r: SimResult | null) => void;
  setHighlight: (kind: HighlightKind) => void;
  setShowTrails: (on: boolean) => void;
  setTheme: (theme: ThemeName) => void;
  resize: () => void;
  configure: (logicalWidth: number, logicalHeight: number, scale: number) => void;
  getLogicalSize: () => { width: number; height: number };
};

type Sample = { t: number; pos: Vec2 };
type Track = Sample[];

export function makeRenderer(canvas: HTMLCanvasElement): Renderer {
  const ctx = getCtx(canvas);
  let result: SimResult | null = null;
  let highlight: HighlightKind = null;
  let showTrails = false;
  let palette: Palette = getPalette("light");
  let logicalWidth = canvas.width;
  let logicalHeight = canvas.height;
  const tracks = new Map<string, Track>();

  function setResult(r: SimResult | null): void {
    result = r;
    tracks.clear();
    if (!r) return;
    for (const e of r.events) {
      if (e.type !== "move") continue;
      const list = tracks.get(e.unitId) ?? [];
      list.push({ t: e.t, pos: { ...e.pos } });
      tracks.set(e.unitId, list);
    }
  }

  function setHighlight(kind: HighlightKind): void {
    highlight = kind;
  }

  function setShowTrails(on: boolean): void {
    showTrails = on;
  }

  function setTheme(theme: ThemeName): void {
    palette = getPalette(theme);
  }

  function alphaFor(kind: Exclude<HighlightKind, null>): number {
    if (highlight === null) return 1;
    return highlight === kind ? 1 : 0.18;
  }

  function unitPosAt(id: string, fallback: Vec2, t: number): Vec2 {
    return unitMotion(id, fallback, t).pos;
  }

  function unitMotion(
    id: string,
    fallback: Vec2,
    t: number,
  ): { pos: Vec2; heading: number | null } {
    const samples = tracks.get(id);
    if (!samples || samples.length === 0) return { pos: fallback, heading: null };
    const first = samples[0];
    if (!first) return { pos: fallback, heading: null };
    if (t <= first.t) return { pos: first.pos, heading: null };
    for (let i = 1; i < samples.length; i++) {
      const prev = samples[i - 1];
      const cur = samples[i];
      if (!prev || !cur) continue;
      if (cur.t >= t) {
        const span = cur.t - prev.t;
        const frac = span > 0 ? (t - prev.t) / span : 0;
        const pos = {
          x: prev.pos.x + (cur.pos.x - prev.pos.x) * frac,
          y: prev.pos.y + (cur.pos.y - prev.pos.y) * frac,
        };
        const dx = cur.pos.x - prev.pos.x;
        const dy = cur.pos.y - prev.pos.y;
        const heading = dx === 0 && dy === 0 ? null : Math.atan2(dy, dx);
        return { pos, heading };
      }
    }
    const last = samples[samples.length - 1];
    return { pos: last ? last.pos : fallback, heading: null };
  }

  function configure(width: number, height: number, scale: number): void {
    logicalWidth = width;
    logicalHeight = height;
    canvas.width = Math.max(1, Math.floor(width * scale));
    canvas.height = Math.max(1, Math.floor(height * scale));
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
  }

  function resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    configure(rect.width, rect.height, dpr);
  }

  function world(): World | null {
    return result ? result.finalWorld : null;
  }

  function worldToCanvas(p: Vec2): Vec2 {
    if (!result) return p;
    return {
      x: (p.x / result.finalWorld.width) * logicalWidth,
      y: (p.y / result.finalWorld.height) * logicalHeight,
    };
  }

  function rangeToCanvas(r: number): number {
    if (!result) return r;
    return (r / result.finalWorld.width) * logicalWidth;
  }

  function drawWaterAndLand(w: World): void {
    ctx.fillStyle = palette.water;
    ctx.fillRect(0, 0, logicalWidth, logicalHeight);
    if (w.country.outline.length === 0) return;
    ctx.beginPath();
    drawSmoothPolygon(w.country.outline);
    ctx.fillStyle = palette.land;
    ctx.fill();
    ctx.lineWidth = palette.coastlineWidth;
    ctx.strokeStyle = palette.coastline;
    ctx.stroke();
  }

  function drawSmoothPolygon(pts: Vec2[]): void {
    if (pts.length === 0) return;
    const cps = pts.map((p) => worldToCanvas(p));
    const n = cps.length;
    const first = cps[0];
    if (!first) return;
    ctx.moveTo((first.x + (cps[n - 1]?.x ?? first.x)) / 2, (first.y + (cps[n - 1]?.y ?? first.y)) / 2);
    for (let i = 0; i < n; i++) {
      const cur = cps[i];
      const next = cps[(i + 1) % n];
      if (!cur || !next) continue;
      const midX = (cur.x + next.x) / 2;
      const midY = (cur.y + next.y) / 2;
      ctx.quadraticCurveTo(cur.x, cur.y, midX, midY);
    }
    ctx.closePath();
  }

  function drawBuiltUp(w: World): void {
    for (const t of w.targets) {
      const pos = worldToCanvas(t.pos);
      const r = (4 + Math.sqrt(Math.max(0, t.value)) / 4);
      const halo = (t.isCapital ? 38 : t.isMajor ? 26 : 18) + r * 1.3;
      const grad = ctx.createRadialGradient(pos.x, pos.y, halo * 0.2, pos.x, pos.y, halo);
      grad.addColorStop(0, t.isMajor ? palette.builtUpMajor : palette.builtUp);
      grad.addColorStop(1, "rgba(0, 0, 0, 0)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, halo, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  function drawRoads(w: World): void {
    if (w.roads.length === 0) return;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (const road of w.roads) {
      if (road.kind !== "motorway") continue;
      ctx.strokeStyle = palette.motorwayCasing;
      ctx.lineWidth = palette.motorwayWidth + 2;
      strokeRoadPath(road.waypoints);
    }
    for (const road of w.roads) {
      if (road.kind === "secondary") {
        ctx.strokeStyle = palette.secondary;
        ctx.lineWidth = palette.secondaryWidth;
      } else {
        ctx.strokeStyle = palette.motorway;
        ctx.lineWidth = palette.motorwayWidth;
      }
      strokeRoadPath(road.waypoints);
    }
  }

  function strokeRoadPath(waypoints: Vec2[]): void {
    if (waypoints.length < 2) return;
    const pts = waypoints.map((p) => worldToCanvas(p));
    ctx.beginPath();
    const first = pts[0];
    if (!first) return;
    ctx.moveTo(first.x, first.y);
    if (pts.length === 2) {
      const last = pts[1];
      if (last) ctx.lineTo(last.x, last.y);
      ctx.stroke();
      return;
    }
    for (let i = 1; i < pts.length - 1; i++) {
      const cur = pts[i];
      const next = pts[i + 1];
      if (!cur || !next) continue;
      const midX = (cur.x + next.x) / 2;
      const midY = (cur.y + next.y) / 2;
      ctx.quadraticCurveTo(cur.x, cur.y, midX, midY);
    }
    const last = pts[pts.length - 1];
    if (last) ctx.lineTo(last.x, last.y);
    ctx.stroke();
  }

  function drawCityLabels(w: World): void {
    for (const t of w.targets) {
      const pos = worldToCanvas(t.pos);
      const r = 4 + Math.sqrt(Math.max(0, t.value)) / 4;
      const dy = -r - 4;
      const label = t.isCapital ? t.name.toUpperCase() : t.name;
      const fontSize = t.isCapital ? 12 : t.isMajor ? 11 : 9.5;
      const fontWeight = t.isCapital ? "700" : t.isMajor ? "600" : "500";
      ctx.font = `${fontWeight} ${fontSize}px "Sen", system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      ctx.lineWidth = 3;
      ctx.strokeStyle = palette.cityLabelHalo;
      ctx.strokeText(label, pos.x, pos.y + dy);
      ctx.fillStyle = t.isCapital ? palette.capitalLabel : palette.cityLabel;
      ctx.fillText(label, pos.x, pos.y + dy);
    }
  }

  function drawFrame(simT: number, _alpha: number): void {
    const w = world();
    if (!w || !result) {
      ctx.fillStyle = palette.water;
      ctx.fillRect(0, 0, logicalWidth, logicalHeight);
      return;
    }

    drawWaterAndLand(w);
    drawBuiltUp(w);
    drawRoads(w);

    if (showTrails) drawTrails(simT, w);

    ctx.globalAlpha = alphaFor("sensor");
    for (const s of w.sensors) {
      drawCircleRing(worldToCanvas(s.pos), rangeToCanvas(s.range), palette.rangeSensor);
    }
    ctx.globalAlpha = alphaFor("fixed");
    for (const fd of w.fixedDefences) {
      drawCircleRing(worldToCanvas(fd.pos), rangeToCanvas(fd.range), palette.rangeFixed);
    }
    ctx.globalAlpha = alphaFor("mobile");
    for (const md of w.mobileDefences) {
      const pos = unitPosAt(md.id, md.patrolPoint, simT);
      drawCircleRing(worldToCanvas(pos), rangeToCanvas(md.engagementRange), palette.rangeMobile);
    }

    ctx.globalAlpha = alphaFor("target");
    for (const t of w.targets) {
      const destroyed = isTargetDestroyedAt(result.events, t.id, simT);
      drawTarget(ctx, worldToCanvas(t.pos), t.value, destroyed, t.isCapital, t.isMajor, palette);
    }

    ctx.globalAlpha = alphaFor("fixed");
    for (const fd of w.fixedDefences) drawFixedDefence(ctx, worldToCanvas(fd.pos), palette);
    ctx.globalAlpha = alphaFor("mobile");
    for (const md of w.mobileDefences) {
      const motion = unitMotion(md.id, md.patrolPoint, simT);
      drawMobileDefence(ctx, worldToCanvas(motion.pos), motion.heading, palette);
    }
    ctx.globalAlpha = alphaFor("sensor");
    for (const s of w.sensors) drawSensor(ctx, worldToCanvas(s.pos), palette);

    ctx.globalAlpha = alphaFor("weapon");
    for (const wpn of allWeapons(result)) {
      const pos = weaponPosAt(wpn, simT, w);
      if (!pos) continue;
      const target = w.targets.find((tg) => tg.id === wpn.targetId);
      const heading = target
        ? Math.atan2(target.pos.y - wpn.origin.y, target.pos.x - wpn.origin.x)
        : 0;
      const detected = wpn.detected || (wpn.detectedAt !== null && wpn.detectedAt <= simT);
      drawStrikeWeapon(ctx, worldToCanvas(pos), heading, detected, palette);
    }
    ctx.globalAlpha = 1;

    for (const e of result.events) {
      if (e.t > simT) continue;
      const ageT = simT - e.t;
      if (e.type === "intercept") {
        if (ageT < MISSILE_FLIGHT_T) {
          const frac = ageT / MISSILE_FLIGHT_T;
          const px = e.from.x + (e.pos.x - e.from.x) * frac;
          const py = e.from.y + (e.pos.y - e.from.y) * frac;
          const heading = Math.atan2(e.pos.y - e.from.y, e.pos.x - e.from.x);
          drawMissile(ctx, worldToCanvas({ x: px, y: py }), heading, 1, palette);
        } else {
          const ringAge = ageT - MISSILE_FLIGHT_T;
          if (ringAge > 1.2) continue;
          const fade = 1 - ringAge / 1.2;
          const cp = worldToCanvas(e.pos);
          ctx.strokeStyle = e.success ? palette.intercept : palette.interceptMiss;
          ctx.globalAlpha = fade;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(cp.x, cp.y, 6 + ringAge * 22, 0, Math.PI * 2);
          ctx.stroke();
          ctx.globalAlpha = 1;
        }
      } else if (e.type === "impact") {
        if (ageT > 1.5) continue;
        const fade = 1 - ageT / 1.5;
        const cp = worldToCanvas(e.pos);
        ctx.fillStyle = palette.impact;
        ctx.globalAlpha = fade * 0.6;
        ctx.beginPath();
        ctx.arc(cp.x, cp.y, 8 + ageT * 14, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    }

    drawCityLabels(w);
  }

  function drawCircleRing(pos: Vec2, radius: number, fillColour: string): void {
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, radius, 0, Math.PI * 2);
    ctx.fillStyle = fillColour;
    ctx.fill();
  }

  function drawTrails(simT: number, w: World): void {
    if (!result) return;
    ctx.lineWidth = 1.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.globalAlpha = highlight === null || highlight === "mobile" ? 0.55 : 0.12;
    ctx.strokeStyle = palette.mobile;
    for (const md of w.mobileDefences) {
      const samples = tracks.get(md.id);
      if (!samples || samples.length < 2) continue;
      ctx.beginPath();
      let started = false;
      for (const s of samples) {
        if (s.t > simT) break;
        const cp = worldToCanvas(s.pos);
        if (!started) { ctx.moveTo(cp.x, cp.y); started = true; }
        else ctx.lineTo(cp.x, cp.y);
      }
      const motion = unitMotion(md.id, md.patrolPoint, simT);
      const cur = worldToCanvas(motion.pos);
      if (started) ctx.lineTo(cur.x, cur.y);
      ctx.stroke();
    }

    ctx.globalAlpha = highlight === null || highlight === "weapon" ? 0.4 : 0.1;
    ctx.lineWidth = 0.8;
    ctx.strokeStyle = palette.weapon;
    for (const wpn of allWeapons(result)) {
      if (simT < wpn.launchedAt) continue;
      const start = worldToCanvas(wpn.origin);
      const endWorld = weaponEndPos(wpn, simT, w);
      if (!endWorld) continue;
      const end = worldToCanvas(endWorld);
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  function weaponEndPos(wpn: StrikeWeapon, simT: number, w: World): Vec2 | null {
    const target = w.targets.find((tg) => tg.id === wpn.targetId);
    if (!target) return null;
    const dx = target.pos.x - wpn.origin.x;
    const dy = target.pos.y - wpn.origin.y;
    const totalDist = Math.hypot(dx, dy);
    if (totalDist === 0) return target.pos;
    const cap = wpn.terminalAt !== null ? Math.min(simT, wpn.terminalAt) : simT;
    const elapsed = Math.max(0, cap - wpn.launchedAt);
    const travelled = Math.min(totalDist, elapsed * wpn.speed);
    return {
      x: wpn.origin.x + (dx / totalDist) * travelled,
      y: wpn.origin.y + (dy / totalDist) * travelled,
    };
  }

  return {
    drawFrame,
    setResult,
    setHighlight,
    setShowTrails,
    setTheme,
    resize,
    configure,
    getLogicalSize: () => ({ width: logicalWidth, height: logicalHeight }),
  };
}

function getCtx(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const c = canvas.getContext("2d");
  if (!c) throw new Error("2d context unavailable");
  return c;
}

function allWeapons(r: SimResult): StrikeWeapon[] {
  return r.waves.flatMap((w) => w.weapons);
}

function weaponPosAt(w: StrikeWeapon, t: number, world: World): Vec2 | null {
  if (t < w.launchedAt) return null;
  const target = world.targets.find((tg: Target) => tg.id === w.targetId);
  if (!target) return null;
  const terminal = w.terminalAt;
  const endT = terminal !== null ? terminal : t;
  if (terminal !== null && t > terminal) return null;
  const dx = target.pos.x - w.origin.x;
  const dy = target.pos.y - w.origin.y;
  const totalDist = Math.hypot(dx, dy);
  if (totalDist === 0) return target.pos;
  const elapsed = Math.max(0, Math.min(endT, t) - w.launchedAt);
  const travelled = Math.min(totalDist, elapsed * w.speed);
  return {
    x: w.origin.x + (dx / totalDist) * travelled,
    y: w.origin.y + (dy / totalDist) * travelled,
  };
}

function isTargetDestroyedAt(events: SimEvent[], targetId: string, t: number): boolean {
  for (const e of events) {
    if (e.type === "targetDestroyed" && e.targetId === targetId && e.t <= t) return true;
  }
  return false;
}
